/* eslint-disable react/jsx-props-no-spreading */
import _ from 'lodash';
import { useRouter } from 'next/router';
import { useTheme } from '@mui/material/styles';
import BackButton from '@mui/icons-material/ArrowBackIosNewOutlined';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import makeClasses from 'hooks/useClasses';
import PageLoader from 'components/Loader/PageLoader';
import PropTypes from 'prop-types';
import React from 'react';
import Stack from '@mui/material/Stack';
import styles from 'styles/jss/components/onboardingStyle';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

const PageContent = ({
  back,
  children,
  loading,
  loadingLabel,
  next,
  nextLabel,
  nextDisabled,
  nextLoading,
  nextLoadingText,
  skip,
  subtitle,
  title
}) => {
  const classes = makeClasses(styles);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const router = useRouter();

  const nextButtonProps = {
    size: 'large',
    className: 'nextButton',
    component: 'button',
    variant: 'contained',
    color: 'primary',
    disabled: nextDisabled,
    loading: nextLoading,
    loadingPosition: 'start',
    fullWidth: true
  };
  const nextButtonText = nextLoadingText && nextLoading ? nextLoadingText : nextLabel;

  const navClick = (action) => {
    if (typeof action === 'function') {
      return action();
    }
    const project = _.get(router, 'query.project');
    return router.push({
      pathname: action,
      query: { project }
    });
  };

  return loading ? (
    <PageLoader loading={loading} loadingLabel={loadingLabel} />
  ) : (
    <Grid
      container
      className={classes.page}
      maxWidth="lg"
    >
      {back && (
        <Box className={classes.backButtonBox}>
          <IconButton
            className="backButton"
            onClick={() => navClick(back)}
          >
            <BackButton />
          </IconButton>
        </Box>
      )}
      <Grid
        item
        xs={10}
        md={8}
        className={classes.pageHeader}
      >
        <Typography variant="pageTitle" align="center" component="h1">
          {title}
        </Typography>
        <Typography variant="subtitle1">
          {subtitle}
        </Typography>
      </Grid>
      <Grid container item className={classes.contentArea}>
        <Grid container item xs={11} md={10} className={classes.content}>
          {children}
        </Grid>
      </Grid>
      {(next || back || skip) && (
        <>
          <Box className={classes.dummyActionArea} />
          <Grid item xs={12} md={8} className={classes.actionArea}>
            <Stack direction="row" spacing={2}>
              {skip && (
                <LoadingButton
                  color="primary"
                  className="skipButton"
                  onClick={() => navClick(skip)}
                >
                  Skip
                </LoadingButton>
              )}
              {back && !isSmall && (
                <LoadingButton
                  component="button"
                  color="primary"
                  onClick={() => navClick(back)}
                >
                  Back
                </LoadingButton>
              )}
              {next && (
                <LoadingButton
                  {...nextButtonProps}
                  onClick={() => navClick(next)}
                >
                  {nextButtonText}
                </LoadingButton>
              )}
            </Stack>
          </Grid>
        </>
      )}
    </Grid>
  );
};

PageContent.defaultProps = {
  next: null,
  nextDisabled: false,
  nextLabel: 'Next',
  nextLoading: false,
  nextLoadingText: null,
  skip: null,
  subtitle: null,
  back: null,
  loading: false,
  loadingLabel: null
};

PageContent.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  next: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  nextLabel: PropTypes.string,
  nextDisabled: PropTypes.bool,
  nextLoading: PropTypes.bool,
  nextLoadingText: PropTypes.string,
  back: PropTypes.string,
  skip: PropTypes.string,
  loading: PropTypes.bool,
  loadingLabel: PropTypes.string
};

export default PageContent;
